import { getGISLayersStatesHost } from '@rsos/utils/metaTags';
import { normalizeServiceUrl } from '../layerAliasMatcher';
import type { ExternalGisLayer } from './externalGisLayers';

const REQUEST_TIMEOUT_MS = 8000;

export type StateCatalogStatus = 'pending' | 'ready' | 'error';

interface GeoServicesLayerRecord {
  name?: unknown;
  state?: unknown;
  gis_service_url?: unknown;
  description?: unknown;
  tags?: unknown;
}

interface GeoServicesLayerResponse {
  data?: GeoServicesLayerRecord[];
}

const stateCatalogStatus = new Map<string, StateCatalogStatus>();
const stateCatalogCache = new Map<string, readonly ExternalGisLayer[]>();
const stateCatalogSubscribers = new Set<() => void>();

function notifySubscribers() {
  stateCatalogSubscribers.forEach(listener => {
    try {
      listener();
    } catch {
      // no-op: a broken listener should not break catalog refreshes
    }
  });
}

export function subscribeStateCatalogUpdates(listener: () => void): () => void {
  stateCatalogSubscribers.add(listener);
  return () => stateCatalogSubscribers.delete(listener);
}

function normalizeStateCode(rawStateCode: unknown): string | undefined {
  if (typeof rawStateCode !== 'string') return undefined;
  const normalized = rawStateCode.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : undefined;
}

function normalizeHost(rawHost: unknown): string | undefined {
  if (typeof rawHost !== 'string') return undefined;
  const trimmed = rawHost.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/\/+$/, '');
}

function getGeoServicesApiHost(): string | undefined {
  return normalizeHost(getGISLayersStatesHost());
}

export function isStateCatalogEndpointConfigured(): boolean {
  return Boolean(getGeoServicesApiHost());
}

function normalizeTags(rawTags: unknown): string[] | undefined {
  if (Array.isArray(rawTags)) {
    const tags = rawTags
      .map(tag => (typeof tag === 'string' ? tag.trim() : ''))
      .filter(Boolean);
    return tags.length > 0 ? tags : undefined;
  }

  if (typeof rawTags === 'string') {
    const tags = rawTags
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);
    return tags.length > 0 ? tags : undefined;
  }

  return undefined;
}

function mapRecordToStateLayer(
  record: GeoServicesLayerRecord,
): ExternalGisLayer | undefined {
  const name =
    typeof record.name === 'string' && record.name.trim()
      ? record.name.trim()
      : '';
  // Keep exact service URL casing from backend. Some ArcGIS servers are
  // case-sensitive on path segments like `/MapServer`, so lowercasing here can
  // turn an otherwise valid layer URL into a failing one.
  const url =
    typeof record.gis_service_url === 'string'
      ? record.gis_service_url.trim()
      : '';
  if (!normalizeServiceUrl(url)) return undefined;

  // Keep this resilient if backend rows occasionally lack a display
  // name; URL fallback avoids dropping otherwise-usable layers.
  const safeName = name || url;

  const category =
    typeof record.state === 'string' && record.state.trim()
      ? record.state.trim().toUpperCase()
      : undefined;

  const description =
    typeof record.description === 'string' && record.description.trim()
      ? record.description.trim()
      : undefined;

  return {
    name: safeName,
    url,
    category,
    description,
    tags: normalizeTags(record.tags),
  };
}

async function fetchStateCatalogFromHost(
  host: string,
  stateCode: string,
): Promise<readonly ExternalGisLayer[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${host}/layers/${stateCode}`, {
      method: 'GET',
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(
        `Geo-services returned ${response.status} for state ${stateCode}`,
      );
    }
    const payload = (await response.json()) as GeoServicesLayerResponse;
    const rows = Array.isArray(payload?.data) ? payload.data : [];
    const stateLayers = rows
      .map(mapRecordToStateLayer)
      .filter((layer): layer is ExternalGisLayer => Boolean(layer));
    return stateLayers;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function fetchStateCatalogFromBackend(
  stateCode: string,
): Promise<readonly ExternalGisLayer[]> {
  const host = getGeoServicesApiHost();
  if (!host) return [];
  return fetchStateCatalogFromHost(host, stateCode);
}

function ensureStateCatalogFetch(stateCode: string): void {
  const status = stateCatalogStatus.get(stateCode);
  if (status === 'pending' || status === 'ready') return;

  stateCatalogStatus.set(stateCode, 'pending');

  void fetchStateCatalogFromBackend(stateCode)
    .then(catalog => {
      stateCatalogCache.set(stateCode, catalog);
      stateCatalogStatus.set(stateCode, 'ready');
      notifySubscribers();
    })
    .catch(() => {
      stateCatalogStatus.set(stateCode, 'error');
      notifySubscribers();
    });
}

/**
 * Returns a cached state-specific catalog when ready. On first call for a
 * state, starts an async fetch and returns `undefined` so callers can fall
 * back while data is loading.
 */
export function getStateCatalog(
  rawStateCode: unknown,
): readonly ExternalGisLayer[] | undefined {
  const stateCode = normalizeStateCode(rawStateCode);
  if (!stateCode) return undefined;

  const status = stateCatalogStatus.get(stateCode);
  if (status === 'ready') return stateCatalogCache.get(stateCode) || [];
  if (status === 'pending') return undefined;

  ensureStateCatalogFetch(stateCode);
  return undefined;
}

/**
 * Read the current lifecycle status for a state's catalog.
 * Returns:
 *   - `pending` while the fetch is in flight
 *   - `ready` after a successful fetch
 *   - `error` if the fetch failed
 *   - `undefined` when no valid state code is available yet
 */
export function getStateCatalogStatus(
  rawStateCode: unknown,
): StateCatalogStatus | undefined {
  const stateCode = normalizeStateCode(rawStateCode);
  if (!stateCode) return undefined;
  return stateCatalogStatus.get(stateCode);
}
